import { api } from '../lib/api-client.js';

import type {
  BulkCreateDevicesInput,
  ComplianceAttachmentPresignInput,
  ComplianceCadence,
  ComplianceLogOutcome,
  ComplianceRecordFiltersQuery,
  ComplianceRecordStatus,
  ComplianceTone,
  ConvertDeviceToTrackedInput,
  CreateDeviceInput,
  CreateDeviceTypeInput,
  DeviceFiltersQuery,
  DeviceTypeFiltersQuery,
  SkipComplianceRecordInput,
  SnoozeComplianceRecordInput,
  SubmitComplianceLogInput,
  UpdateDeviceInput,
  UpdateDeviceTypeInput,
} from '@sitewise/shared';

/* ----------------------------- Types --------------------------------- */

export interface DeviceTypeSummary {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  isComplianceTracked: boolean;
  defaultCadence: ComplianceCadence | null;
  defaultCustomIntervalDays: number | null;
  defaultGracePeriodDays: number;
  requiresUnit: boolean;
  icon: string | null;
  regulatoryNotes: string | null;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceSummary {
  id: string;
  orgId: string;
  buildingId: string;
  unitId: string | null;
  deviceTypeId: string;
  label: string;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  installedAt: string | null;
  warrantyExpiresOn: string | null;
  purchasePrice: string | null;
  purchaseVendor: string | null;
  cadenceOverride: ComplianceCadence | null;
  customIntervalDaysOverride: number | null;
  gracePeriodDaysOverride: number | null;
  isComplianceTrackedOverride: boolean | null;
  nextDueAt: string | null;
  lastLoggedAt: string | null;
  isActive: boolean;
  documentAttachmentIds: string[];
  notes: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceRecordSummary {
  id: string;
  orgId: string;
  deviceId: string;
  buildingId: string;
  unitId: string | null;
  dueAt: string;
  graceUntilAt: string;
  status: ComplianceRecordStatus;
  tone: ComplianceTone;
  loggedAt: string | null;
  logId: string | null;
  autoWorkOrderId: string | null;
  notificationState: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceLogSummary {
  id: string;
  orgId: string;
  complianceRecordId: string;
  deviceId: string;
  loggedByUserId: string | null;
  loggedByVendorUserId: string | null;
  loggedByKind: string;
  performedOn: string;
  outcome: ComplianceLogOutcome;
  notes: string | null;
  nextDueOverride: string | null;
  signatureAttachmentId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentLite {
  id: string;
  kind: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
  caption: string | null;
  uploadedAt: string;
  signedUrl: string | null;
}

export interface ComplianceDashboard {
  counts: Record<ComplianceRecordStatus, number>;
  totalActive: number;
  topOverdue: Array<{
    recordId: string;
    deviceId: string;
    deviceLabel: string;
    buildingId: string;
    buildingName: string;
    dueAt: string;
    daysOverdue: number;
  }>;
  upcomingNextSevenDays: Array<{
    recordId: string;
    deviceId: string;
    deviceLabel: string;
    buildingId: string;
    buildingName: string;
    dueAt: string;
    daysUntilDue: number;
  }>;
}

export interface RecordWithDeviceType extends ComplianceRecordSummary {
  device: DeviceSummary;
  deviceType: DeviceTypeSummary;
}

export interface DeviceWithDeviceType extends DeviceSummary {
  deviceType: DeviceTypeSummary;
}

export interface PresignResponse {
  attachment: AttachmentLite;
  uploadId: string;
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
  expiresAt: string;
}

/* ----------------------------- Helpers ------------------------------- */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/* ----------------------------- Device types -------------------------- */

export async function listDeviceTypes(filters: Partial<DeviceTypeFiltersQuery> = {}) {
  return api<{ deviceTypes: DeviceTypeSummary[] }>(
    `/device-types${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchDeviceType(id: string) {
  return api<{ deviceType: DeviceTypeSummary }>(`/device-types/${id}`);
}

export async function createDeviceType(input: CreateDeviceTypeInput) {
  return api<{ deviceType: DeviceTypeSummary }>('/device-types', {
    method: 'POST',
    body: input,
  });
}

export async function updateDeviceType(id: string, input: UpdateDeviceTypeInput) {
  return api<{ deviceType: DeviceTypeSummary }>(`/device-types/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function archiveDeviceType(id: string) {
  await api(`/device-types/${id}`, { method: 'DELETE' });
}

export async function seedDefaultDeviceTypes() {
  return api<{
    result: { inserted: number; skipped: number; insertedSlugs: string[] };
  }>('/device-types/seed-defaults', { method: 'POST' });
}

/* ----------------------------- Devices ------------------------------- */

export async function listDevices(filters: Partial<DeviceFiltersQuery> = {}) {
  return api<{ devices: DeviceWithDeviceType[] }>(
    `/devices${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchDevice(id: string) {
  return api<{
    device: DeviceSummary;
    deviceType: DeviceTypeSummary;
    documents: AttachmentLite[];
  }>(`/devices/${id}`);
}

export async function createDevice(input: CreateDeviceInput) {
  return api<{ device: DeviceSummary; deviceType: DeviceTypeSummary }>('/devices', {
    method: 'POST',
    body: input,
  });
}

export async function updateDevice(id: string, input: UpdateDeviceInput) {
  return api<{ device: DeviceSummary; deviceType: DeviceTypeSummary }>(`/devices/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function archiveDevice(id: string) {
  await api(`/devices/${id}`, { method: 'DELETE' });
}

export async function bulkCreateDevices(input: BulkCreateDevicesInput) {
  return api<{ created: DeviceSummary[] }>('/devices/bulk', {
    method: 'POST',
    body: input,
  });
}

export async function convertDeviceToTracked(id: string, input: ConvertDeviceToTrackedInput) {
  return api<{ device: DeviceSummary; deviceType: DeviceTypeSummary }>(
    `/devices/${id}/convert-to-tracked`,
    { method: 'POST', body: input },
  );
}

export async function presignDeviceDocument(
  deviceId: string,
  input: ComplianceAttachmentPresignInput,
) {
  return api<PresignResponse>(`/devices/${deviceId}/documents/presign`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmDeviceDocument(deviceId: string, attachmentId: string) {
  return api<{ attachment: AttachmentLite }>(
    `/devices/${deviceId}/documents/${attachmentId}/confirm`,
    { method: 'POST' },
  );
}

/* ----------------------------- Records / Logs ------------------------ */

export async function listComplianceRecords(filters: Partial<ComplianceRecordFiltersQuery> = {}) {
  return api<{ records: RecordWithDeviceType[] }>(
    `/compliance/records${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchComplianceRecord(id: string) {
  return api<{
    record: ComplianceRecordSummary;
    device: DeviceSummary;
    deviceType: DeviceTypeSummary;
    pendingAttachments: AttachmentLite[];
  }>(`/compliance/records/${id}`);
}

export async function submitComplianceLog(recordId: string, input: SubmitComplianceLogInput) {
  return api<{
    log: ComplianceLogSummary;
    nextRecord: ComplianceRecordSummary | null;
    followUpWorkOrderId: string | null;
  }>(`/compliance/records/${recordId}/log`, {
    method: 'POST',
    body: input,
  });
}

export async function skipComplianceRecord(recordId: string, input: SkipComplianceRecordInput) {
  return api<{ record: ComplianceRecordSummary }>(`/compliance/records/${recordId}/skip`, {
    method: 'POST',
    body: input,
  });
}

export async function snoozeComplianceRecord(recordId: string, input: SnoozeComplianceRecordInput) {
  return api<{ record: ComplianceRecordSummary }>(`/compliance/records/${recordId}/snooze`, {
    method: 'POST',
    body: input,
  });
}

export async function fetchComplianceDashboard() {
  return api<ComplianceDashboard>('/compliance/dashboard');
}

export async function listRecentComplianceLogs() {
  return api<{
    logs: Array<{
      record: ComplianceRecordSummary;
      log: ComplianceLogSummary;
      device: DeviceSummary;
      deviceType: DeviceTypeSummary;
    }>;
  }>('/compliance/recent-logs');
}

export async function presignLogAttachment(
  recordId: string,
  input: ComplianceAttachmentPresignInput,
) {
  return api<PresignResponse>(`/compliance/records/${recordId}/attachments/presign`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmLogAttachment(recordId: string, attachmentId: string) {
  return api<{ attachment: AttachmentLite }>(
    `/compliance/records/${recordId}/attachments/${attachmentId}/confirm`,
    { method: 'POST' },
  );
}

/* ----------------------------- Certificate ingest -------------------- */

export interface ComplianceIngestJob {
  id: string;
  filename: string;
  contentType: string;
  status: string;
  suggestion: {
    deviceTypeSlug: string | null;
    buildingId: string | null;
    deviceId: string | null;
    performedOn: string | null;
    outcome: string | null;
    make?: string | null;
    model?: string | null;
    serialNumber?: string | null;
    suggestedNextDueOn?: string | null;
    summary: string;
    confidence: number;
    reasoning?: string;
  } | null;
  confidence: number | null;
  errorMessage: string | null;
  appliedComplianceRecordId: string | null;
  appliedLogId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listComplianceIngestJobs() {
  return api<{ jobs: ComplianceIngestJob[] }>('/compliance/ingest');
}

export async function createComplianceIngestJob(input: {
  uploadId: string;
  filename?: string;
}) {
  return api<{ job: ComplianceIngestJob }>('/compliance/ingest', {
    method: 'POST',
    body: input,
  });
}

export async function confirmComplianceIngestJob(
  jobId: string,
  input: {
    deviceId: string;
    performedOn: string;
    outcome: 'passed' | 'passed_with_notes' | 'failed' | 'requires_followup';
    notes?: string | null;
    nextDueOverride?: string | null;
  },
) {
  return api<{ job: ComplianceIngestJob }>(`/compliance/ingest/${jobId}/confirm`, {
    method: 'POST',
    body: input,
  });
}
